import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Mounted once at AppRoutes level. When `user` becomes truthy (logged in)
 * we fetch `/api/announcements/active`. If it returns a non-null banner,
 * we display the popup modal. Backend enforces interval per banner
 * (default 8 jam) via impression tracker — jadi frontend cukup polling
 * rutin (8 menit) untuk deteksi kapan interval sudah lewat & banner
 * berikutnya siap ditampilkan tanpa harus reload halaman.
 *
 * No localStorage — impressions are server-side so state survives
 * cross-device login.
 */
export default function AnnouncementPopup({ user }) {
  const [banner, setBanner] = useState(null);
  const [open, setOpen] = useState(false);
  const [imageOk, setImageOk] = useState(true);

  useEffect(() => {
    if (!user) {
      setBanner(null);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/announcements/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const data = res.data;
        if (data && (data.image_url || data.title)) {
          setBanner(data);
          setImageOk(true);
          setOpen(true);
        }
      } catch (e) {
        // Silent fail — popup is optional
        // eslint-disable-next-line no-console
        console.debug('announcement fetch failed', e?.response?.status);
      }
    };
    // Initial fetch — small delay after login to avoid stealing focus.
    const t = setTimeout(fetchActive, 600);
    // Feb 9, 2026 — Background polling tiap 8 menit. Backend menentukan
    // interval per banner (default 8 jam); frontend cukup poll rutin agar
    // popup muncul lagi begitu interval habis, tanpa perlu refresh halaman.
    const POLL_INTERVAL_MS = 8 * 60 * 1000; // 8 menit
    const poll = setInterval(fetchActive, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
      clearInterval(poll);
    };
  }, [user]);

  if (!open || !banner) return null;

  const imageSrc = banner.image_url
    ? banner.image_url.startsWith('http')
      ? banner.image_url
      : `${BACKEND_URL}${banner.image_url}`
    : null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="announcement-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Announcement"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="announcement-popup"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Tutup"
          data-testid="announcement-popup-close"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors backdrop-blur-sm"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image (full-width inside modal). Hidden if missing/broken. */}
        {imageSrc && imageOk ? (
          <img
            src={imageSrc}
            alt={banner.title || 'Announcement'}
            className="w-full h-auto block max-h-[80vh] object-contain bg-gray-100"
            onError={() => setImageOk(false)}
            data-testid="announcement-popup-image"
          />
        ) : (
          <div className="p-10 text-center bg-gradient-to-br from-blue-50 to-indigo-100">
            <h3 className="text-xl font-bold text-gray-900">{banner.title || 'Pengumuman'}</h3>
            <p className="text-sm text-gray-600 mt-2">Vaxa Medika Internal</p>
          </div>
        )}

        {/* Caption — only shown when image present (subtle title under image) */}
        {imageSrc && imageOk && banner.title && (
          <div className="px-4 py-3 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-800 text-center">{banner.title}</p>
          </div>
        )}
      </div>
    </div>
  );
}
